import React, { FC, useEffect, useState } from 'react'

import { FeesTab } from '../../../FeesTab/FeesTab'
import { ClassesTab } from '../../../ClassesTab/ClassesTab'
import { UserRequiredFieldsCard } from '../../../../cards/UserRequiredFieldsCard/UserRequiredFieldsCard'
import { RidingDetailsCard } from '../../../../cards/RidingDetailsCard/RidingDetailsCard'
import { UserProfileCard } from '../../../../cards/UserProfileCard/UserProfileCard'
import { UserMembershipsCard } from '../../../../cards/UserMembershipsCard/UserMembershipsCard'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../../../../../ui/accordion'
import { HorsesTab } from '../../../HorsesTab/HorsesTab'
import { TeamTab } from '../../../TeamTab/TeamTab'
import { PaperworkTab } from '../../../PaperworkTab/PaperworkTab'
import { TicketsTab } from '../../../TicketsTab/TicketsTab'
import { Skeleton } from '../../../../../../../ui/skeleton'
import { TeamDescription } from '../TeamDescription/TeamDescription'

import { getUserFullName } from '../../../../../../../../helpers/helpers'
import customImageComponent from '../../../../../../../common/CustomImageComponent'
import {
  getRegistrationEventDataInDb,
  listenRegistrationItems,
} from '../../../../../../../../helpers/registration-on-event'
import {
  getRiderTeamMembers,
  haveTeamMember,
  isValidTeamMember,
} from '../../../../../../../../helpers/teamMembers'
import { getRidersByPaperwork } from '../../../../../../../../helpers/paperwork'

import UsersDocumentsService from '../../../../../../../../services/firebase-service/users-documents/UsersDocumentsService'

import { IRiderTeamMemberInterface } from '../../../../../../../../models/rider-team-member/riderTeamMember.interface'
import { IUserInterface } from '../../../../../../../../models/users/user.interface'
import { IRegistrationByDayInterface } from '../../../../../../../../models/registrations-by-day/registrationByDay.interface'
import { IEventReviewPublish } from '../../../../../../../../models/event-review-publish/event-review-publish.interface'
import { ICount, IUserInterfaceTeamRegistrar } from '../../TeamRegistrarTab'
import { IRegistrationTicketInterface } from '../../../../../../../../models/registration-tickets/registrationTicket.interface'
import { IRegistrationFeesInterface } from '../../../../../../../../models/registration-fees/registrationFees.interface'
import { IUserDocument } from '../../../../../../../../models/user-documents/user-documents.interface'

import { RIDER_TABS } from '../../../../../RiderInfoModal/RiderInfoModal'
import { CONST } from '../../../../../../../../const/const'

interface TeamItemProps {
  index: number
  item: IUserInterfaceTeamRegistrar
  setUser: (value: IUserInterface) => void
  organizer: IUserInterface | null
  riderTeamMembers: IRiderTeamMemberInterface[]
  eventRequiredUserFields?: string[]
  registrar: IUserInterface | null
  eventId: string
  registrationId: string
  eventReviewPublish: IEventReviewPublish | null
  defaultOpenedPrimaryAccordion?: string
  defaultOpenedSecondaryAccordion?: 'classes' | 'fees' | 'tickets' | 'team'
}

export const TeamItem: FC<TeamItemProps> = ({
  index,
  item,
  setUser,
  registrar,
  defaultOpenedPrimaryAccordion,
  defaultOpenedSecondaryAccordion,
  riderTeamMembers,
  eventRequiredUserFields,
  registrationId,
  eventId,
  organizer,
  eventReviewPublish,
}) => {
  const [count, setCount] = useState<ICount>({
    classes: 0,
    fees: 0,
    tickets: 0,
    paperwork: 0,
    horses: 0,
  })
  const [loading, setLoading] = useState<number | null>(index)
  const [registrationsByDay, setRegistrationsByDay] = useState<IRegistrationByDayInterface[]>([])
  const [registrationsFees, setRegistrationsFees] = useState<IRegistrationFeesInterface[]>([])
  const [registrationsTickets, setRegistrationsTickets] = useState<IRegistrationTicketInterface[]>(
    []
  )
  const [usersDocuments, setUsersDocuments] = useState<IUserDocument[]>([])

  const roles = new Set(
    riderTeamMembers
      .filter((current) => current.teamMemberId === item.id)
      .map((current) => current.teamMemberRole)
  )

  if (item.id === registrar?.id) roles.add('Registrar')

  useEffect(() => {
    const getItemsData = async () => {
      try {
        setLoading(index)
        const {
          registrationsByDay: registrationsByDayInDb,
          registrationsFees: registrationsFeesInDb,
          registrationsTickets: registrationsTicketsInDb,
        } = await getRegistrationEventDataInDb({
          registrationId,
          ownerId: item.id,
          type: ['classes', 'fees', 'tickets'],
        })

        const usersDocumentsInDb = await UsersDocumentsService.getByRider(registrationId, item.id)

        const registrationsByDayByUser = registrationsByDayInDb.filter(
          (current) => current.riderId === item.id
        )

        setRegistrationsByDay(registrationsByDayByUser)
        setRegistrationsFees(registrationsFeesInDb)

        setRegistrationsTickets(registrationsTicketsInDb)
        setUsersDocuments(usersDocumentsInDb)
      } catch (error) {
        console.error('error: ', error)
      } finally {
        setLoading(null)
      }
    }
    getItemsData().then()
  }, [index, item.id, registrationId])

  useEffect(() => {
    const unsubscribe = () => {
      listenRegistrationItems({
        registrationId,
        ownerId: item.id,
        type: ['classes', 'fees', 'tickets'],
        setRegistrationsByDay,
        setRegistrationsFees,
        setRegistrationsTickets,
      })
      UsersDocumentsService.listenByRider(registrationId, item.id, setUsersDocuments)
    }

    return () => {
      unsubscribe()
    }
  }, [item.id, registrar?.id, registrationId])

  useEffect(() => {
    const horsesIds = new Set()
    registrationsByDay.forEach((registrationByDay) => {
      registrationByDay.horsesSelected?.forEach((horse) => {
        horsesIds.add(horse.id)
      })
    })

    let paperworkIssues = 0
    const allRiders = getRidersByPaperwork(registrationsByDay)
    allRiders.forEach((registrationByDay) => {
      const teamMembers = getRiderTeamMembers(registrationByDay, riderTeamMembers, usersDocuments)
      const haveRider = haveTeamMember(teamMembers, CONST.UI.TEAM_MEMBERS_ROLES.RIDER)
      if (!haveRider) paperworkIssues += 1
      paperworkIssues += isValidTeamMember(teamMembers)
    })

    setCount({
      classes: registrationsByDay.length,
      fees: registrationsFees.length,
      tickets: registrationsTickets.length,
      paperwork: paperworkIssues,
      horses: horsesIds.size,
    })
  }, [
    registrationsByDay,
    registrationsFees.length,
    registrationsTickets.length,
    riderTeamMembers,
    usersDocuments,
  ])

  return (
    <Accordion
      className="border-black/10 border rounded-lg px-6 py-4"
      type="single"
      collapsible
      defaultValue={defaultOpenedPrimaryAccordion}
    >
      <AccordionItem value={item.id}>
        <AccordionTrigger>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="w-[45px] h-[45px] min-w-[45px] rounded-md overflow-hidden border border-black/10">
              {customImageComponent(
                item.userProfilePicture,
                getUserFullName(item),
                'w-full h-full object-cover'
              )}
            </div>
            <div>
              <h5 className="text-base text-black">
                {getUserFullName(item)} - {[...roles].join(', ')}
              </h5>
              <div className="mt-1">
                {loading === index ? (
                  <Skeleton className="h-5 w-full rounded" />
                ) : (
                  <TeamDescription
                    count={count}
                    registrationsByDay={registrationsByDay}
                    registrationsFees={registrationsFees}
                    registrationsTickets={registrationsTickets}
                  />
                )}
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="grid grid-cols-1 md:grid-cols-2 auto-rows-min items-start gap-5">
          <div className="flex flex-col gap-5">
            <UserRequiredFieldsCard
              isCollapsible
              user={item}
              setUser={setUser}
              eventRequiredUserFields={eventRequiredUserFields}
              isEditable={true}
            />
            <UserProfileCard isCollapsible user={item} setUser={setUser} isEditable={true} />
            <RidingDetailsCard isCollapsible user={item} setUser={setUser} isEditable={true} />
            <UserMembershipsCard isCollapsible user={item} setUser={setUser} isEditable={true} />
          </div>
          <div className="flex flex-col gap-5">
            <ClassesTab
              isOpenedDefault={defaultOpenedSecondaryAccordion === RIDER_TABS.CLASSES.value}
              type="registrar"
              isCollapsible
              registrarId={registrar?.id || null}
              registrationId={registrationId}
              eventId={eventId}
              organizer={organizer}
              user={item}
              publishedEvent={eventReviewPublish}
            />
            <FeesTab
              isOpenedDefault={defaultOpenedSecondaryAccordion === RIDER_TABS.FEES.value}
              type="registrar"
              isCollapsible
              registrarId={registrar?.id || null}
              registrationId={registrationId}
              eventId={eventId}
              publishedEvent={eventReviewPublish}
              user={item}
              organizer={organizer}
            />
            <TicketsTab
              isOpenedDefault={defaultOpenedSecondaryAccordion === RIDER_TABS.TICKETS.value}
              type="registrar"
              isCollapsible
              registrarId={registrar?.id || null}
              registrationId={registrationId}
              eventId={eventId}
              publishedEvent={eventReviewPublish}
              user={item}
              organizer={organizer}
            />
            <PaperworkTab
              isCollapsible
              type="registrar"
              registrationsByDay={registrationsByDay}
              eventId={eventId}
              userId={item.id}
              registrationId={registrationId}
            />
            {roles.has(CONST.UI.TEAM_MEMBERS_ROLES.RIDER) && (
              <TeamTab
                isOpenedDefault={defaultOpenedSecondaryAccordion === RIDER_TABS.TEAM.value}
                isCollapsible
                type="registrar"
                registrationId={registrationId}
                user={item}
                publishedEvent={eventReviewPublish}
              />
            )}
            <HorsesTab
              isCollapsible
              activeTab="registrar"
              registrationsByDay={registrationsByDay}
              publishedEvent={eventReviewPublish}
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
